import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/**
 * AnimatedCounter — animates a numeric value from 0 to `target`.
 * Supports optional prefix/suffix (like "$", "B+"). Non-numeric values are rendered as-is.
 */
export default function AnimatedCounter({
  target,
  prefix = "",
  suffix = "",
  duration = 1800,
  decimals = 0,
  className = "",
  format = (n, d) => n.toLocaleString(undefined, { maximumFractionDigits: d }),
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (typeof target !== "number") return;
    let raf;
    const start = performance.now();
    const from = 0;
    const to = target;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  const display =
    typeof target === "number" ? format(value, decimals) : String(target);

  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
